import { useCallback, useEffect, useMemo, useState } from "react";
import { pluralize } from "../../utils/pluralize";
import {
    DatePickerSectionWrapper,
    DatePickerTitle,
    DatePickerSubtitle,
    DatePickerWrapper,
    DatePickerSectionTitleWrapper,
    DatePickerSectionCalendarButtonWrapper,
    DatePickerSectionCalendarBackButton,
} from "./DatepickerBookingStyling";

import moment from "moment-timezone";
import { Datepicker, MbscDatepickerValue, momentTimezone } from "@mobiscroll/react";
import { DatePicker } from "anystay-ui";
import ContinueBtn from "../btn/continueBtn/continueBtn";
import useClickOutside from "../../hook/useClickOutside";
import AnimationWrapperForPopup from "../animationWrapper/AnimationWrapperForPopup";
import useIsDesktopSize from "../../hook/useIsDesktopSize";
import { ListingModel } from "../../interfaces/models/listingModel";
import { UnifiedListingModel } from "../../interfaces/models/UnifiedListingModel";
import { isHotel } from "../../utils/listingUtils";

momentTimezone.moment = moment;
interface DatepickerListingInterface extends UnifiedListingModel {
    // advanceNotice and advanceNoticeTime are already defined in UnifiedListingModel as optional
    // We'll handle the undefined case in the component logic
}

interface DatepickerBookingProps {
    handleClickOutside: () => void;
    startDate: string | Date | null;
    endDate: string | Date | null;
    listing: DatepickerListingInterface;
    saveFunc: (arg: Date[]) => Promise<void>;
    cancelFunc: () => void;
    unavailableDates?: { date: string; checkOutPossible: boolean }[];
    calendarObjects?: Array<{
        date: string | Date;
        blocked?: boolean;
        quantity?: number;
        reserved?: number;
        checkOutAvailable?: boolean;
        status?: string;
        checkInAvailable?: boolean;
        minimumStayLength?: number;
        maximumStayLength?: number;
        advanceNotice?: number;
        advanceNoticeTime?: number;
    }>;
}

export default function DatepickerBooking({ handleClickOutside, startDate, endDate, listing, saveFunc, cancelFunc, unavailableDates = [], calendarObjects }: DatepickerBookingProps) {
    const [dateRange, setDateRange] = useState<MbscDatepickerValue | string[]>([startDate !== "anyArrival" ? startDate : null, endDate !== "anyDeparture" ? endDate : null]);
    const [maxDate, setMaxDate] = useState<Date | null>(null);
    const [minDate, setMinDate] = useState<Date | null>(null);
    const [isContinueBtnLoading, setIsContinueBtnLoading] = useState(false);
    const isDesktopSize = useIsDesktopSize();
    const wrapperRef = useClickOutside(true, () => {
        if (!isContinueBtnLoading) {
            handleClickOutside();
        }
    });

    const setMaxDateFunc = useCallback((listing: DatepickerListingInterface) => {
        // For hotels, allow booking up to 24 months in advance
        if (isHotel(listing.id)) {
            const maxDate = new Date();
            maxDate.setMonth(maxDate.getMonth() + 24);
            setMaxDate(maxDate);
            return;
        }

        let maxDateIncrementMonths = 0;
        if (listing.availabilityRange === "3-months") {
            maxDateIncrementMonths = 3;
        } else if (listing.availabilityRange === "6-months") {
            maxDateIncrementMonths = 6;
        } else if (listing.availabilityRange === "12-months") {
            maxDateIncrementMonths = 12;
        } else if (listing.availabilityRange === "18-months") {
            maxDateIncrementMonths = 18;
        } else if (listing.availabilityRange === "24-months" || listing.availabilityRange === "all-future-dates") {
            maxDateIncrementMonths = 24;
        }
        let maxDate = null;
        const currentDate = new Date();
        if (listing.availabilityRange) {
            maxDate = new Date(currentDate);
            maxDate.setMonth(maxDate.getMonth() + maxDateIncrementMonths);
        }
        setMaxDate(maxDate);
    }, []);

    const setMinDateFunc = useCallback((listing: DatepickerListingInterface) => {
        // For hotels, allow same-day booking
        if (isHotel(listing.id)) {
            const minDate = new Date();
            setMinDate(minDate);
            return;
        }

        let minDate = new Date();
        const time = getTimeInInt(new Date());
        if (listing.advanceNotice && listing.advanceNotice > 0) {
            minDate.setDate(minDate.getDate() + listing.advanceNotice);
        } else {
            if (listing.advanceNoticeTime && time > listing.advanceNoticeTime) {
                minDate.setDate(minDate.getDate() + 1);
            }
        }
        setMinDate(minDate);
    }, []);

    useEffect(() => {
        setMaxDateFunc(listing);
        setMinDateFunc(listing);
        if (startDate && endDate && startDate !== "anyArrival" && endDate !== "anyDeparture") {
            let startDateObj = new Date(startDate);
            let endDateObj = new Date(endDate);
            setDateRange([startDateObj, endDateObj]);
        }
    }, [endDate, listing, setMaxDateFunc, setMinDateFunc, startDate]);

    const formatMonth = (month: number) => {
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return monthNames[month];
    };

    const formatDateToString = (date: Date) => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = formatMonth(date.getMonth());
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
    };

    const getTimeInInt = (date: Date) => {
        const objDate = new Date(date);
        return objDate.getHours() * 100 + objDate.getMinutes();
    };

    const getNumberOfNights = (startDate: Date, endDate: Date) => {
        const diffTime = endDate.getTime() - startDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    // Centralized normalization of unavailable dates from raw calendarObjects or provided unavailableDates
    const normalizedUnavailableDates = useMemo(() => {
        if (Array.isArray(calendarObjects) && calendarObjects.length > 0) {
            return calendarObjects
                .filter((obj: any) => {
                    const isBlocked = obj?.blocked === true || obj?.status === 'blocked';
                    const qty = typeof obj?.quantity === 'number' ? obj.quantity : undefined;
                    const rsv = typeof obj?.reserved === 'number' ? obj.reserved : 0;
                    const soldOut = typeof qty === 'number' ? (qty - rsv) <= 0 : false;
                    return isBlocked || soldOut;
                })
                .map((obj: any) => ({
                    date: typeof obj.date === 'string' ? (obj.date.includes('T') ? obj.date.split('T')[0] : obj.date) : new Date(obj.date).toISOString().split('T')[0],
                    checkOutPossible: Boolean(obj?.checkOutAvailable),
                }));
        }
        // Fallback to pre-normalized shape
        return (unavailableDates || []).map((d) => ({
            date: typeof d.date === 'string' ? (d.date.includes('T') ? d.date.split('T')[0] : d.date) : new Date(d.date as any).toISOString().split('T')[0],
            checkOutPossible: d.checkOutPossible,
        }));
    }, [calendarObjects, unavailableDates]);

    // Derive constraints from calendarObjects
    const calendarConstraints = useMemo(() => {
        if (!Array.isArray(calendarObjects) || calendarObjects.length === 0) return {} as {
            minStay?: number; maxStay?: number; minDate?: Date;
        };

        const valid = calendarObjects.filter((o: any) => o && (o.blocked !== true) && ((typeof o.quantity !== 'number') || ((o.quantity - (o.reserved || 0)) > 0)));
        let minStay: number | undefined = undefined;
        let maxStay: number | undefined = undefined;
        let minAdvance: number | undefined = undefined;
        let minAdvanceTime: number | undefined = undefined;

        for (const o of valid) {
            if (typeof o.minimumStayLength === 'number') {
                minStay = typeof minStay === 'number' ? Math.min(minStay, o.minimumStayLength) : o.minimumStayLength;
            }
            if (typeof o.maximumStayLength === 'number') {
                maxStay = typeof maxStay === 'number' ? Math.max(maxStay, o.maximumStayLength) : o.maximumStayLength;
            }
            if (typeof o.advanceNotice === 'number') {
                minAdvance = typeof minAdvance === 'number' ? Math.min(minAdvance, o.advanceNotice) : o.advanceNotice;
            }
            if (typeof o.advanceNoticeTime === 'number') {
                minAdvanceTime = typeof minAdvanceTime === 'number' ? Math.min(minAdvanceTime, o.advanceNoticeTime) : o.advanceNoticeTime;
            }
        }

        let minDate: Date | undefined = undefined;
        try {
            const today = new Date();
            const timeInt = today.getHours() * 100 + today.getMinutes();
            let daysToAdd = Math.max(0, minAdvance ?? 0);
            if (typeof minAdvanceTime === 'number' && timeInt > minAdvanceTime) {
                daysToAdd = daysToAdd + 1;
            }
            const candidate = new Date();
            candidate.setDate(candidate.getDate() + daysToAdd);
            minDate = candidate;
        } catch {}

        return { minStay, maxStay, minDate };
    }, [calendarObjects]);

    let dateRangeForMobileDatePicker = [];
    if ((dateRange as Array<Date | null | string>)[0]) {
        const date = (dateRange as Array<Date | null | string>)[0];
        if (date) {
            dateRangeForMobileDatePicker.push(new Date(date).toISOString().slice(0, 10));
        }
    }
    if ((dateRange as Array<Date | null | string>)[1]) {
        const date = (dateRange as Array<Date | null | string>)[1];
        if (date) {
            dateRangeForMobileDatePicker.push(new Date(date).toISOString().slice(0, 10));
        }
    }

    return (
        <AnimationWrapperForPopup>
            <DatePickerSectionWrapper ref={wrapperRef}>
                <DatePickerTitle>
                    {dateRange && (dateRange as Array<Date | null | string>)[0] && (dateRange as Array<Date | null | string>)[1]
                        ? (() => {
                              const nights = getNumberOfNights((dateRange as Date[])[0], (dateRange as Date[])[1]);
                              return `${nights} ${pluralize(nights, 'night', 'nights')}`;
                          })()
                        : "Choose your dates"}
                </DatePickerTitle>
                <DatePickerSubtitle>
                    {dateRange && (dateRange as Array<Date | null | string>)[0] && (dateRange as Array<Date | null | string>)[1]
                        ? `${formatDateToString((dateRange as Date[])[0])} - ${formatDateToString((dateRange as Date[])[1])}`
                        : "Select your dates for exact pricing"}
                </DatePickerSubtitle>
                <DatePickerWrapper>
                    {isDesktopSize ? (
                        <Datepicker
                            select="range"
                            returnFormat="jsdate"
                            value={dateRange}
                            min={(calendarConstraints.minDate || minDate) ?? undefined}
                            max={maxDate ? maxDate : undefined}
                            timezonePlugin={momentTimezone}
                            dataTimezone={"Australia/Brisbane"}
                            displayTimezone={"Australia/Brisbane"}
                            minRange={isHotel(listing.id) ? 1 : ((calendarConstraints.minStay ?? listing.minimumStay ?? 1) + 1)}
                            maxRange={isHotel(listing.id) ? 30 : ((calendarConstraints.maxStay ?? listing.maximumStay ?? 365) + 1)}
                            exclusiveEndDates={false}
                            pages={"auto"}
                            touchUi={!isDesktopSize}
                            animate={false}
                            theme={"material"}
                            themeVariant={"light"}
                            showRangeLabels={false}
                            showOuterDays={false}
                            display={"inline"}
                            invalid={isHotel(listing.id) ? [] : normalizedUnavailableDates}
                            rangeEndInvalid={!isHotel(listing.id)}
                            inRangeInvalid={false}
                            onChange={(dateRange) => {
                                setDateRange(dateRange.value);
                            }}
                        />
                    ) : (
                        <DatePicker
                            value={dateRangeForMobileDatePicker}
                            subtractMonthNumber={1}
                            totalMonthNumber={20}
                            blockCells={normalizedUnavailableDates.map((date) => date.date)}
                            onSelect={(prop) => {
                                if (prop.startDate && prop.endDate) {
                                    setDateRange([new Date(prop.startDate), new Date(prop.endDate)]);
                                } else {
                                    setDateRange([new Date(prop.startDate)]);
                                }
                            }}
                            minRange={isHotel(listing.id) ? 1 : (calendarConstraints.minStay ?? listing.minimumStay ?? 1)}
                            maxRange={isHotel(listing.id) ? 30 : (calendarConstraints.maxStay ?? listing.maximumStay ?? 365)}
                        />
                    )}
                </DatePickerWrapper>
                <DatePickerSectionCalendarButtonWrapper>
                    <DatePickerSectionCalendarBackButton onClick={cancelFunc} type="button">
                        Cancel
                    </DatePickerSectionCalendarBackButton>
                    <ContinueBtn
                        disabled={!(dateRange as Array<Date | null | string>)[0] || !(dateRange as Array<Date | null | string>)[1] || isContinueBtnLoading}
                        onClick={async () => {
                            setIsContinueBtnLoading(true);
                            await saveFunc(dateRange as Date[]);
                            cancelFunc();
                        }}
                        continueType="button"
                        background={"black"}
                        style={{
                            width: "unset",
                        }}
                        isLoading={isContinueBtnLoading}
                    >
                        Save
                    </ContinueBtn>
                </DatePickerSectionCalendarButtonWrapper>
            </DatePickerSectionWrapper>
        </AnimationWrapperForPopup>
    );
}
